import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import TagsInput from './TagsInput';
import FileUpload from './FileUpload';

/**
 * Reusable "Edit" dialog. Has TWO modes:
 *   1) Default (uncontrolled) — renders a Pencil button trigger and manages its own open state.
 *   2) Controlled — pass `open` + `onOpenChange` (and optionally `hideTrigger`) so a
 *      parent (eg RowActions dropdown) can drive the dialog.
 *
 * Props:
 *   item       — the row being edited
 *   endpoint   — REST collection path (e.g. "suppliers")
 *   title      — dialog title
 *   fields     — [{ key, label, type, options?, rows?, placeholder?, hint? }]
 *                  type: text | email | tel | number | textarea | select
 *                  options (for select): [{ value, label }]
 *   onSaved    — callback after successful save
 *   testIdPrefix — base for data-testids (defaults to endpoint)
 *   buttonClassName — optional override for the trigger button style
 *   buttonVariant   — defaults to "ghost"
 *   open       — (optional) controlled open state; when present, dialog is controlled
 *   onOpenChange — (optional) controlled onOpenChange handler
 *   hideTrigger  — (optional) suppress the built-in pencil button (use with controlled mode)
 */
export default function EditEntityDialog({
  item,
  endpoint,
  title = 'Edit',
  fields = [],
  onSaved,
  testIdPrefix,
  buttonClassName = 'text-amber-600 hover:bg-amber-50',
  buttonVariant = 'ghost',
  open: openProp,
  onOpenChange: onOpenChangeProp,
  hideTrigger = false,
}) {
  const isControlled = typeof openProp === 'boolean';
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (v) => { isControlled ? onOpenChangeProp?.(v) : setInternalOpen(v); };
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const prefix = testIdPrefix || endpoint;

  useEffect(() => {
    if (open) {
      const next = {};
      fields.forEach(f => { next[f.key] = item?.[f.key] ?? (f.type === 'number' ? 0 : ''); });
      setDraft(next);
    }
  }, [open, item, fields]);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      // Coerce number fields
      fields.forEach(f => {
        if (f.type === 'number' && payload[f.key] !== '' && payload[f.key] != null) {
          payload[f.key] = Number(payload[f.key]);
        }
      });
      await axios.put(`${API}/${endpoint}/${item.id}`, payload, { withCredentials: true });
      toast.success(`${title} saved`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <>
      {!hideTrigger && (
        <Button
          type="button"
          size="sm"
          variant={buttonVariant}
          data-testid={`edit-${prefix}-${item.id}`}
          onClick={() => setOpen(true)}
          className={buttonClassName}
        >
          <Pencil className="w-4 h-4" />
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
            {fields.map(f => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea
                    data-testid={`edit-${prefix}-${f.key}-input`}
                    rows={f.rows || 3}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                ) : f.type === 'select' ? (
                  <Select value={String(draft[f.key] ?? '')} onValueChange={(v) => set(f.key, v)}>
                    <SelectTrigger data-testid={`edit-${prefix}-${f.key}-select`}><SelectValue placeholder={f.placeholder || 'Select…'} /></SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map(o => (
                        <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === 'tags' ? (
                  <TagsInput value={draft[f.key] || []} onChange={(arr) => set(f.key, arr)} testIdPrefix={`edit-${prefix}-${f.key}`} />
                ) : f.type === 'image' ? (
                  <FileUpload
                    value={draft[f.key] ? [draft[f.key]] : []}
                    onChange={(urls) => set(f.key, urls[urls.length - 1] || '')}
                    maxFiles={1}
                    accept="image/*"
                    showCamera
                  />
                ) : (
                  <Input
                    data-testid={`edit-${prefix}-${f.key}-input`}
                    type={f.type || 'text'}
                    value={draft[f.key] ?? ''}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                  />
                )}
                {f.hint && <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>}
              </div>
            ))}
            <Button
              data-testid={`save-${prefix}-edit-btn`}
              onClick={save}
              disabled={saving}
              className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900"
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
